/**
 * Weeks, streaks and ranking — all of it pure, none of it in SQL.
 *
 * The week is the household's week, not UTC's: a job done at 23:30 on Sunday
 * belongs to the week that is ending, and one done at 00:30 on Monday starts
 * the new one. That boundary has to be computed in a named timezone, across
 * DST, or the leaderboard resets at the wrong moment twice a year and nobody
 * can say why.
 */

export const DEFAULT_TIMEZONE = "Europe/Amsterdam";

/** Milliseconds the named zone is ahead of UTC at that instant. */
export function zoneOffset(timestamp: number, timeZone: string): number {
  const format = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const parts: Record<string, string> = {};
  for (const part of format.formatToParts(new Date(timestamp))) parts[part.type] = part.value;
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour) % 24,
    Number(parts.minute),
    Number(parts.second),
  );
  return asUtc - timestamp;
}

/**
 * The instant a given local wall-clock time occurs at.
 *
 * Applied twice on purpose: the first offset is read at the wrong instant
 * whenever the guess lands on the other side of a DST change, and the second
 * pass corrects it.
 */
export function zonedTimeToUtc(year: number, month: number, day: number, hour: number, timeZone: string): Date {
  const guess = Date.UTC(year, month - 1, day, hour);
  const corrected = guess - zoneOffset(guess, timeZone);
  return new Date(guess - zoneOffset(corrected, timeZone));
}

/** The local calendar date at an instant, as `YYYY-MM-DD`. */
export function localDate(instant: Date, timeZone: string): string {
  const format = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return format.format(instant);
}

export interface Week {
  /** Inclusive. */
  start: Date;
  /** Exclusive — the next week's start, so no completion can fall between them. */
  end: Date;
  /** The Monday, as `YYYY-MM-DD` local. */
  label: string;
}

/**
 * The household week containing `now`, or `weeksAgo` weeks before it.
 *
 * Monday-start: a chore week that resets mid-weekend would split exactly the
 * two days most of the work happens on.
 */
export function weekOf(now: Date, timeZone: string, weeksAgo = 0): Week {
  const [year, month, day] = localDate(now, timeZone).split("-").map(Number) as [number, number, number];
  // The local calendar date is already known, so its weekday is a property of
  // the date itself — read it off a UTC-anchored copy rather than converting
  // back and forth. Sunday is 7, not 0, so Monday-start arithmetic is a
  // subtraction rather than a special case.
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay() || 7;
  const daysSinceMonday = weekday - 1 + weeksAgo * 7;

  const startLocal = new Date(Date.UTC(year, month - 1, day) - daysSinceMonday * 86_400_000);
  const start = zonedTimeToUtc(
    startLocal.getUTCFullYear(),
    startLocal.getUTCMonth() + 1,
    startLocal.getUTCDate(),
    0,
    timeZone,
  );
  const endLocal = new Date(startLocal.getTime() + 7 * 86_400_000);
  const end = zonedTimeToUtc(endLocal.getUTCFullYear(), endLocal.getUTCMonth() + 1, endLocal.getUTCDate(), 0, timeZone);

  return { start, end, label: localDate(start, timeZone) };
}

/**
 * Consecutive days up to today on which someone did at least one job.
 *
 * Yesterday still counts as alive: a streak that dies at midnight before
 * anyone has had breakfast punishes the wrong thing.
 */
export function streakDays(dates: Iterable<string>, today: string): number {
  const done = new Set(dates);
  const cursor = new Date(`${today}T00:00:00Z`);
  if (!done.has(today)) {
    cursor.setUTCDate(cursor.getUTCDate() - 1);
    if (!done.has(cursor.toISOString().slice(0, 10))) return 0;
  }
  let streak = 0;
  while (done.has(cursor.toISOString().slice(0, 10))) {
    streak += 1;
    cursor.setUTCDate(cursor.getUTCDate() - 1);
  }
  return streak;
}

export interface Scored {
  personId: number;
  name: string;
  emoji: string;
  /** True when a face has been synced and can be served from our own origin. */
  hasAvatar?: boolean;
  kind: "parent" | "kid" | "guest";
  points: number;
  jobs: number;
  minutes: number;
  streakDays: number;
}

export interface Ranked extends Scored {
  /** 1-based. Equal scores share a rank, and the next rank skips accordingly. */
  rank: number;
  /** True only when nobody else has as many jobs, and at least one was done. */
  leader: boolean;
}

/**
 * Rank by points earned.
 *
 * Points already carry the size of the job and the bounty it grew while it
 * waited, so ranking on them rewards both doing more and rescuing what has
 * been ignored. Ties break on jobs, then minutes, then name.
 *
 * Ranking on raw minutes would still hand every week to whoever mows the lawn;
 * the boards stay split by kind so a four-year-old is never ranked against an
 * adult regardless.
 */
export function rank(scores: Scored[]): Ranked[] {
  const sorted = [...scores].sort(
    (a, b) => b.points - a.points || b.jobs - a.jobs || b.minutes - a.minutes || a.name.localeCompare(b.name),
  );

  const best = sorted[0]?.points ?? 0;
  const leadersAtTop = sorted.filter((s) => s.points === best).length;

  const ranked: Ranked[] = [];
  let previousPoints: number | null = null;
  let previousRank = 0;
  sorted.forEach((score, index) => {
    const position = score.points === previousPoints ? previousRank : index + 1;
    previousPoints = score.points;
    previousRank = position;
    ranked.push({ ...score, rank: position, leader: best > 0 && score.points === best && leadersAtTop === 1 });
  });
  return ranked;
}

/** 🥇🥈🥉 for the first three ranks, then the number. */
export function medal(rank: number): string {
  return ["🥇", "🥈", "🥉"][rank - 1] ?? `${rank}`;
}
